import React, { memo, useCallback, useMemo } from "react";

import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CircleOutlineCheckIcon, PrintConversationIcon } from "modules/icons";
import { usePrint } from "modules/providers/print-provider";
import {
  DeleteJobMutation,
  DeleteJobMutationVariables,
  JobQuery,
  JobQueryVariables,
} from "gql/graphql";
import JOB_QUERY from "./networking/job";
import colors from "app/colors";
import DELETE_JOB_MUTATION from "modules/drawers/networking/delete-job";
import { useOverlay } from "modules/providers/overlay-provider";
import { DRAWER_TYPE } from "modules/providers/overlay-provider/overlay-provider";

interface IProps {
  jobId: string;
}

const PrintConversationModal = ({ jobId }: IProps) => {
  const { closeModal, openDrawer } = useOverlay();
  const { print } = usePrint();

  const [deleteJobAPI] = useMutation<
    DeleteJobMutation,
    DeleteJobMutationVariables
  >(DELETE_JOB_MUTATION);

  const { data } = useQuery<JobQuery, JobQueryVariables>(JOB_QUERY, {
    variables: {
      id: jobId,
    },
  });

  const inlineUrl = useMemo(() => data?.job?.inlineUrl, [data]);

  const onPrintClicked = useCallback(() => {
    if (inlineUrl) {
      print(inlineUrl);
      closeModal();
    }
  }, [closeModal, inlineUrl, print]);

  const onDownloadsAndExportsClicked = useCallback(() => {
    openDrawer({
      type: DRAWER_TYPE.DOWNLOADS_EXPORTS,
    });
    closeModal();
  }, [closeModal, openDrawer]);

  const onCancelClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      if (jobId) {
        deleteJobAPI({
          variables: {
            id: jobId,
          },
        });
      }
      closeModal();
    },
    [closeModal, deleteJobAPI, jobId],
  );

  const onCloseClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      closeModal();
    },
    [closeModal],
  );

  return (
    <Modal isOpen onClose={closeModal} size="xs" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16} p={4}>
        <ModalHeader>
          <Center>
            <VStack>
              <PrintConversationIcon boxSize={10} />
              <Text pt={2}>Print Conversation</Text>
            </VStack>
          </Center>
          {/*Confirmed that the modal close button cancels */}
          {!inlineUrl && <ModalCloseButton onClick={onCancelClicked} />}
        </ModalHeader>
        <ModalBody p={0}>
          <VStack w="full">
            {!inlineUrl ? (
              <HStack>
                <Spinner size="sm" />
                <Text fontSize={14}>PDF processing, please wait...</Text>
              </HStack>
            ) : (
              <Text fontSize={14} fontWeight={700}>
                <CircleOutlineCheckIcon
                  boxSize={4}
                  stroke={colors.greenPrimary}
                  mr={2}
                />
                PDF ready to print
              </Text>
            )}
            <HStack pt={4}>
              <Button
                variant="cancel"
                height={12}
                w={28}
                onClick={onCloseClicked}
              >
                Close
              </Button>
              <Button
                height={12}
                w={28}
                isDisabled={!inlineUrl}
                onClick={onPrintClicked}
              >
                Print
              </Button>
            </HStack>
            {!inlineUrl && (
              <Text fontSize={14} color={colors.gray50} align="left">
                <Text fontWeight={700} as="span">
                  Don't want to wait?
                </Text>{" "}
                Find it later in
                <br />
                <Button
                  variant="link"
                  onClick={onDownloadsAndExportsClicked}
                  color={colors.blue}
                  fontSize={14}
                >
                  "Downloads & Exports"
                </Button>{" "}
                via the navigation bar
              </Text>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(PrintConversationModal);
